/**
 * Utilities for image-anchored scenes: orient the camera and scene root for a
 * desktop 3D preview that matches the perspective the user will see in WebXR.
 *
 * The {@link ImageAnchor} behavior owns the marker preview mesh itself; this
 * module only handles camera + scene-root pose for desktop viewing.
 */

import type { TraceViewer } from '../core/TraceViewer';
import type { TraceScene } from '../core/TraceScene';
import type { SceneObject } from '../core/SceneObject';
import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import type { Camera } from '@babylonjs/core/Cameras/camera';
import { ImageAnchor } from '../behaviors/imageanchor';
import { AnchorOrientation } from '../scenedata/common';
import { resolveAnchorPitchRadiansForRoot } from './anchorOrientationHeuristic';
import { Logger } from '../utils/Logger';
import { editorRootsUnder, mountEditorRoot, remountEditorRoots } from '../editor/authoring/documentRoot';
import { placementRotationFromPitch, type PlacementPose, type PlacementRotation } from '../cms/localizers';

/**
 * The node an {@link ImageAnchor} lives on.
 *
 * Under D8 the CMS-managed anchor is a root-entity behavior and lands on the
 * **document root** (SCENE_ROOT_SPEC §5.1) — the file's own root node, which
 * is also what the anchor rotation has to pose, since the content hangs below
 * it. A session with no document root (a legacy flat `.zip`, an anchor
 * attached before any content loaded) keeps it on the engine root, which is
 * where it has always been.
 *
 * Decided by where the behavior IS, not by shape: one answer for both eras,
 * and load, re-commit and export cannot disagree about which node they mean.
 */
export function anchorHost(traceScene: TraceScene | null | undefined): SceneObject | null {
    if (!traceScene) return null;
    const documentRoot = traceScene.getDocumentRoot();
    if (documentRoot?.getBehavior(ImageAnchor)) return documentRoot;
    return traceScene.getSceneRoot();
}

/**
 * Default screen fraction for the image marker in the desktop preview.
 * Matches the XR alignment overlay (MOBILE_SCREEN_FRACTION = 0.7).
 * The landing page may pass a different value based on its actual
 * rendered image size to keep the 2D→3D transition seamless.
 */
const TARGET_SCREEN_FRACTION = 0.7;

/** How far the camera pulls back from the initial match, as a multiplier on radius. */
const PULLBACK_RADIUS_SCALE = 3.0;

/** Duration of the pull-back animation in milliseconds. */
const PULLBACK_DURATION_MS = 1500;

/**
 * Compute the camera distance at which an object of `widthMeters` fills
 * `targetFraction` of the smaller screen dimension.
 *
 * Derives the distance from the camera's vertical FOV and the actual
 * screen aspect ratio (via `window`). This avoids depending on the
 * projection matrix, which may reflect a 1×1 px preload canvas rather
 * than the real viewport.
 *
 * The formula matches ImageAlignmentOverlay's projection-matrix approach
 * when the canvas matches the display:
 *   screenFraction = width * scaleForMinDim / (2 * distance)
 *   distance = width * scaleForMinDim / (2 * screenFraction)
 */
export function computeViewingDistance(
    camera: Camera,
    widthMeters: number,
    targetFraction: number = TARGET_SCREEN_FRACTION,
): number {
    // 1/tan(fov/2) — vertical projection scale (M[1][1] equivalent)
    const vScale = 1 / Math.tan(camera.fov / 2);

    // Determine real screen aspect. Fall back to 1 (square) if unavailable.
    const screenW = typeof window !== 'undefined' ? window.innerWidth : 1;
    const screenH = typeof window !== 'undefined' ? window.innerHeight : 1;
    const aspect = screenW / screenH;

    // Scale factor for the smaller screen dimension.
    // Landscape (aspect >= 1): smaller dim = height → vScale.
    // Portrait  (aspect <  1): smaller dim = width  → vScale / aspect.
    const minDimScale = aspect >= 1 ? vScale : vScale / aspect;

    return widthMeters * minDimScale / (2 * targetFraction);
}

/**
 * Attach an {@link ImageAnchor} behavior to the scene root, rendering a
 * preview of the marker at physical scale. Idempotent — calling it twice
 * with different settings updates the existing behavior.
 *
 * Use this from consumer apps that load a `.trace` file directly and need
 * to overlay the marker preview separately from the scene's authored
 * content. `loadCmsScene` already does this for you.
 *
 * @param viewer - The trace-viewer element with a loaded scene
 * @param imageUrl - Fully-qualified marker image URL
 * @param widthMeters - Physical width of the printed marker in meters
 * @param orientation - Expected pose; defaults to Free.
 */
export async function attachImageAnchor(
    viewer: TraceViewer,
    imageUrl: string,
    widthMeters: number,
    orientation: AnchorOrientation = AnchorOrientation.Free,
): Promise<void> {
    // The root entity, per §5.1: the document root when the session has one.
    const sceneRoot = viewer.traceScene?.getSessionDocumentRoot() ?? viewer.traceScene?.getSceneRoot();
    if (!sceneRoot) {
        Logger.warn('[attachImageAnchor] No scene root');
        return;
    }
    await sceneRoot.setBehavior(ImageAnchor, {
        src: imageUrl,
        widthMeters,
        orientation,
    });
}

/**
 * Whether the desktop preview frames the marker head-on (`true`) rather than
 * from above. It reads the pitch {@link applyAnchorRotation} poses the host
 * with ({@link resolveAnchorPitchRadiansForRoot}), so the framing always
 * matches the pose: an orientation, an authored pitch, or for a `Free` marker
 * without one, the content.
 *
 * Of the two views it picks the one that sees more of the marker's face:
 * head-on sees `cos(pitch)` of it, from above `-sin(pitch)`. So a pitch from
 * -45° to 135° is framed head-on, and the rest from above. A marker pitched
 * between 90° and 180° faces down and away, so it is seen from behind in
 * both; there is no view from below.
 */
export function resolveAnchorIsVertical(sceneRoot: SceneObject): boolean {
    const anchor = sceneRoot.getBehavior(ImageAnchor);
    const pitch = resolveAnchorPitchRadiansForRoot(
        anchor?.orientation,
        anchor?.pitchDegrees,
        sceneRoot.getChildren(),
    );
    return Math.cos(pitch) >= -Math.sin(pitch);
}

/**
 * Apply scene-root pose for the desktop preview of an image-anchored scene.
 *
 * Operates in the **OpenXR anchor frame** (after the `.zip` loader's
 * Unity LH → OpenXR-RH conversion):
 *   - `+X` = image-right.
 *   - `+Y` = image-top.
 *   - `+Z` = image-normal (out of the marker face, toward viewer).
 *
 * Pose is a pure pitch about local X (image-right), with no yaw or roll,
 * from {@link resolveAnchorPitchRadiansForRoot}:
 *   - **Vertical**: scene-root identity. Marker stands up at the
 *     origin with normal world +Z (facing the default camera).
 *   - **Horizontal**: scene-root `Rx(-π/2)`. Marker lies flat at the
 *     origin with normal world +Y (camera looks down at it).
 *   - **Free or unset with a non-zero `pitchDegrees`**: `Rx(pitch)`.
 *   - **Free or unset with no pitch**: the content-detection heuristic,
 *     upright when there is no content.
 *
 * Also lifts the anchor Y so all content sits above the ground plane. An
 * editor root under the host stays in the scene frame (`mountEditorRoot`).
 *
 * Desktop-preview only — XR drives scene-root pose via the WebXR
 * reference-space offset and must not be combined with this transform.
 */
export function applyAnchorRotation(traceScene: TraceScene): void {
    const sceneRoot = anchorHost(traceScene);
    if (!sceneRoot) return;

    // The resolver `ImageAnchor._applySceneRootRotation` and the camera
    // framing share, so load, re-commit and framing never disagree about
    // what a `Free` marker means.
    const anchor = sceneRoot.getBehavior(ImageAnchor);
    const explicit = anchor?.orientation;
    const pitch = resolveAnchorPitchRadiansForRoot(
        explicit,
        anchor?.pitchDegrees,
        sceneRoot.getChildren(),
    );
    const rot = Quaternion.RotationYawPitchRoll(0, pitch, 0);
    sceneRoot.transform.rotationQuaternion = rot;
    Logger.log(`[applyAnchorRotation] orientation=${explicit ?? 'unset'}, pitch=${(pitch * 180 / Math.PI).toFixed(2)}°`);

    // Lift the anchor so all content sits above the ground plane (y=0).
    // After rotation correction, some content may extend below y=0. Editor
    // content is not the scene's: it is left out of the lift and mounted in
    // the scene frame below.
    const contentChildren = sceneRoot.getChildren();
    const editorRoots = contentChildren.length > 0 ? editorRootsUnder(sceneRoot) : [];
    if (contentChildren.length > 0) {
        sceneRoot.transform.computeWorldMatrix(true);

        let minY = Infinity;
        for (const child of contentChildren) {
            for (const mesh of child.transform.getChildMeshes(false)) {
                if (editorRoots.some(root => mesh.isDescendantOf(root.transform))) continue;
                mesh.computeWorldMatrix(true);
                const worldMinY = mesh.getBoundingInfo().boundingBox.minimumWorld.y;
                if (worldMinY < minY) {
                    minY = worldMinY;
                }
            }
        }

        if (isFinite(minY) && minY < 0) {
            sceneRoot.transform.position.y -= minY;
            Logger.log(`[applyAnchorRotation] Anchor lifted by ${(-minY).toFixed(4)}m to keep content above ground`);
        }
    }
    for (const root of editorRoots) mountEditorRoot(root, root.getParent());
}

/**
 * Frame the camera on the image marker, head-on or from above
 * ({@link resolveAnchorIsVertical}), after {@link applyAnchorRotation} has
 * run. Sets target, radius, beta (tilt), and clipping planes so the desktop
 * preview matches the landing-page image at the chosen screen fraction.
 */
export function frameAnchorCamera(traceScene: TraceScene, imageWidthMeters: number, screenFraction?: number): void {
    const sceneRoot = anchorHost(traceScene);
    if (!sceneRoot) return;
    const camera = traceScene.getCamera();
    if (!camera) return;

    // Recompute world matrices after scene root rotation + lift.
    sceneRoot.transform.computeWorldMatrix(true);
    for (const child of sceneRoot.getChildren()) {
        for (const mesh of child.transform.getChildMeshes(false)) {
            mesh.computeWorldMatrix(true);
        }
    }

    const isVertical = resolveAnchorIsVertical(sceneRoot);

    // The anchor position is the scene root's world-space origin —
    // this is where the image marker sits after rotation + lift.
    const anchorPos = sceneRoot.transform.getAbsolutePosition();

    // Compute viewing distance so the image marker fills the same
    // screen fraction as the landing-page preview image.
    const fraction = screenFraction ?? TARGET_SCREEN_FRACTION;
    const radius = Math.max(computeViewingDistance(camera, imageWidthMeters, fraction), 0.01);

    // Target the anchor, not the bounds center.
    camera.target.copyFrom(anchorPos);

    // Remove the lower radius limit set by calculateSceneBoundingBox
    // during startScene — for large scenes, that limit (radius * 0.05)
    // can prevent the camera from getting close enough to the small
    // image marker.
    camera.lowerRadiusLimit = 0;
    camera.radius = radius;

    if (isVertical) {
        // Wall marker: scene-root identity. Anchor +Z normal stays at
        // world +Z; anchor +Y image-top stays at world +Y. Camera at
        // α=+π/2, β=π/2 sits at world +Z looking back at origin with
        // screen-up = world +Y, so image-top reads at screen-top.
        camera.alpha = Math.PI / 2;
        camera.beta = Math.PI / 2;
    } else {
        // Table marker: scene-root Rx(-π/2). Anchor +Z normal → world
        // +Y; anchor +Y image-top → world -Z. Camera at α=+π/2,
        // β≈0 sits above the marker (world +Y) looking down with
        // screen-up = world -Z — matching image-top so the printed
        // top reads at screen-top. β=0.01 avoids ArcRotateCamera
        // gimbal-lock at the pole.
        camera.alpha = Math.PI / 2;
        camera.beta = 0.01;
    }

    // Fixed generous clipping planes — image-anchored scenes range
    // from tiny markers (~0.1m) to large content several meters out.
    camera.minZ = 0.01;
    camera.maxZ = 100;

    Logger.log(
        `[frameAnchorCamera] target=(${anchorPos.x.toFixed(3)}, ${anchorPos.y.toFixed(3)}, ${anchorPos.z.toFixed(3)}), ` +
        `radius=${radius.toFixed(3)}, beta=${camera.beta.toFixed(3)}, imageWidth=${imageWidthMeters.toFixed(3)}`,
    );
}

/**
 * Apply orientation transforms to the scene root and camera for desktop preview
 * of image-anchored scenes. Composes {@link applyAnchorRotation} (scene-root
 * pose + lift-above-ground) with {@link frameAnchorCamera} (camera framing).
 *
 * Call once after loading an image-anchored scene. To re-apply just the
 * scene-root pose (e.g. after the user changes anchor orientation in the
 * editor), call {@link applyAnchorRotation} directly — that avoids yanking
 * the camera back to head-on view.
 *
 * Not needed in XR — the real-world pose is determined by image alignment.
 *
 * @param viewer - The trace-viewer element with a loaded scene
 * @param imageWidthMeters - Physical width of the image marker in meters
 */
export function alignAnchorToWorld(viewer: TraceViewer, imageWidthMeters: number, screenFraction?: number): void {
    const traceScene = viewer.traceScene;
    if (!traceScene) return;
    applyAnchorRotation(traceScene);
    frameAnchorCamera(traceScene, imageWidthMeters, screenFraction);
}

/**
 * Pose the anchor host at a placement's `T_scene←L` (TraceBackend#999).
 *
 * The placement counterpart of {@link applyAnchorRotation}: the host — and
 * the marker preview on it — lands at `pose` in the scene frame (the
 * editor world), and content hangs below it in the marker frame, exactly
 * as on the legacy path. A migrated placement carries `q = Rx(θ)`, the
 * rotation `applyAnchorRotation` would have set, so the two paths agree
 * (golden: `test/vitest/fixtures/localizer-legacy-rotation.golden.json`).
 *
 * Unlike `applyAnchorRotation` this does NOT lift content above `y = 0`:
 * with placements the scene frame is authored, and the host has to sit
 * exactly at `P` for placement gizmos to line up with the marker.
 *
 * `preserveContentWorldPose` re-expresses the host's direct children so
 * their world poses stay put — for an editor moving the primary placement
 * (the marker moves, the content doesn't), like `ImageAnchor`'s
 * orientation re-commit. Off for a load: then the content is in the marker
 * frame and must follow it, except an editor root, which stays in the scene
 * frame (`remountEditorRoots`).
 *
 * Desktop/editor only, like `applyAnchorRotation`: an XR session poses the
 * mount as `T_world←L · P⁻¹` instead.
 */
export function applyPlacementAnchorPose(
    traceScene: TraceScene,
    pose: PlacementPose,
    options: { preserveContentWorldPose?: boolean } = {},
): void {
    const host = anchorHost(traceScene);
    if (!host) return;
    const rotation = new Quaternion(pose.q[0], pose.q[1], pose.q[2], pose.q[3]);
    const position = new Vector3(pose.t[0], pose.t[1], pose.t[2]);
    if (options.preserveContentWorldPose) {
        reposePreservingChildren(host, position, rotation);
    } else {
        host.transform.position.copyFrom(position);
        host.transform.rotationQuaternion = rotation;
        remountEditorRoots(host);
    }
    Logger.log(`[applyPlacementAnchorPose] t=(${pose.t.map(v => v.toFixed(3)).join(', ')}), q=(${pose.q.map(v => v.toFixed(4)).join(', ')})`);
}

/**
 * Move `host` to a new local pose while its direct children keep their world
 * poses: `child' = newHost⁻¹ · oldHost · child`.
 *
 * Works in the host's PARENT frame, so it preserves world poses only while
 * that parent is fixed during the call. That holds for every anchor host
 * today: the document root or the engine root, both under the mount. It is
 * exact whatever the parent's own transform is, because both poses are
 * expressed in the same parent frame.
 */
function reposePreservingChildren(host: SceneObject, position: Vector3, rotation: Quaternion): void {
    const oldRot = host.transform.rotationQuaternion?.clone()
        ?? Quaternion.FromEulerVector(host.transform.rotation);
    const oldPos = host.transform.position.clone();
    const newRotInv = Quaternion.Inverse(rotation);
    for (const child of host.getChildren()) {
        const localRot = child.transform.rotationQuaternion?.clone()
            ?? Quaternion.FromEulerVector(child.transform.rotation);
        // Parent-space pose of the child, taken through the old host pose
        // and back through the new one (scale is left alone: placements are
        // rigid and the host's scale is not touched).
        const inParent = child.transform.position.multiply(host.transform.scaling);
        inParent.rotateByQuaternionToRef(oldRot, inParent);
        inParent.addInPlace(oldPos).subtractInPlace(position);
        inParent.rotateByQuaternionToRef(newRotInv, inParent);
        child.transform.position.copyFrom(inParent.divide(host.transform.scaling));
        child.transform.rotationQuaternion = newRotInv.multiply(oldRot.multiply(localRot));
    }
    host.transform.position.copyFrom(position);
    host.transform.rotationQuaternion = rotation;
}

/**
 * Resolve a `legacy_heuristic` placement's rotation the way the legacy path
 * resolves a `Free`/unset marker with no pitch: from the host's content
 * ({@link resolveAnchorPitchRadiansForRoot}). `orientation` is what the
 * legacy path would hand the anchor — `Vertical` for a brand-new scene with
 * no content row, else the accessor's.
 *
 * `host` is the node the anchor is (about to be) attached to. Call it after
 * the content has loaded and before the host is posed; the result is what
 * an editor writes back as an `explicit` rotation.
 */
export function resolveLegacyHeuristicRotation(
    host: SceneObject,
    orientation: AnchorOrientation | undefined,
    pitchDegrees?: number,
): PlacementRotation {
    const pitch = resolveAnchorPitchRadiansForRoot(
        orientation,
        pitchDegrees,
        host.getChildren(),
    );
    return placementRotationFromPitch(pitch);
}

/**
 * Frame the camera head-on to a marker posed at `pose` — the placement
 * counterpart of {@link frameAnchorCamera}. The camera sits on the marker's
 * `+Z` normal looking back at it; when the normal is (nearly) vertical, the
 * azimuth is chosen so the marker's image-top reads at screen-top. For the
 * legacy poses this gives `frameAnchorCamera`'s angles: `α = π/2` with
 * `β = π/2` (wall) or `β = 0.01` (table).
 */
export function framePlacementCamera(
    traceScene: TraceScene,
    pose: PlacementPose,
    imageWidthMeters: number,
    screenFraction?: number,
): void {
    const camera = traceScene.getCamera();
    if (!camera) return;
    const host = anchorHost(traceScene);
    host?.transform.computeWorldMatrix(true);

    const worldRotation = host?.transform.absoluteRotationQuaternion
        ?? new Quaternion(pose.q[0], pose.q[1], pose.q[2], pose.q[3]);
    const normal = new Vector3(0, 0, 1).applyRotationQuaternion(worldRotation);
    const top = new Vector3(0, 1, 0).applyRotationQuaternion(worldRotation);
    const target = host?.transform.getAbsolutePosition().clone()
        ?? new Vector3(pose.t[0], pose.t[1], pose.t[2]);

    const fraction = screenFraction ?? TARGET_SCREEN_FRACTION;
    camera.target.copyFrom(target);
    camera.lowerRadiusLimit = 0;
    camera.radius = Math.max(computeViewingDistance(camera, imageWidthMeters, fraction), 0.01);

    const POLE = 0.01;
    camera.beta = Math.min(Math.max(Math.acos(Math.max(-1, Math.min(1, normal.y))), POLE), Math.PI - POLE);
    camera.alpha = Math.abs(normal.y) > 0.999
        // Looking along ±Y: screen-up is −(cos α, 0, sin α) at the pole.
        ? Math.atan2(-top.z * Math.sign(normal.y), -top.x * Math.sign(normal.y))
        : Math.atan2(normal.z, normal.x);

    camera.minZ = 0.01;
    camera.maxZ = 100;
}

/**
 * Pose the host from a placement and frame the camera on its marker — the
 * placement counterpart of {@link alignAnchorToWorld}.
 */
export function alignPlacementToWorld(
    viewer: TraceViewer,
    pose: PlacementPose,
    imageWidthMeters: number,
    screenFraction?: number,
): void {
    const traceScene = viewer.traceScene;
    if (!traceScene) return;
    applyPlacementAnchorPose(traceScene, pose);
    framePlacementCamera(traceScene, pose, imageWidthMeters, screenFraction);
}

/**
 * Smoothly pull the camera back from the head-on anchor view so the user
 * can see the 3D scene around the marker. Call this once the viewer is
 * visible and rendering (not during preload).
 *
 * Eases the camera radius outward to reveal more of the scene while
 * keeping the same viewing angle. Optionally cross-fades the scene
 * background color from one value to another.
 *
 * @param viewer - The trace-viewer element (must have an active scene + camera)
 * @param fromBg - Starting background hex color (e.g. landing page bg)
 * @param toBg - Target background hex color (e.g. viewer bg)
 */
export function animateCameraPullback(
    viewer: TraceViewer,
    fromBg?: string,
    toBg?: string,
): void {
    const traceScene = viewer.traceScene;
    if (!traceScene) return;

    const camera = traceScene.getCamera();
    if (!camera) return;

    const startRadius = camera.radius;
    const endRadius = startRadius * PULLBACK_RADIUS_SCALE;

    const scene = traceScene.getBabylonScene();

    // Parse background colors for cross-fade
    let startColor: Color3 | null = null;
    let endColor: Color3 | null = null;
    if (fromBg && toBg) {
        try {
            startColor = Color3.FromHexString(fromBg);
            endColor = Color3.FromHexString(toBg);
            scene.clearColor = new Color4(startColor.r, startColor.g, startColor.b, 1);
        } catch {
            // Invalid hex — skip color animation
        }
    }

    let startTime = -1;

    const observer = scene.onBeforeRenderObservable.add(() => {
        if (startTime < 0) {
            startTime = performance.now();
        }

        const t = Math.min((performance.now() - startTime) / PULLBACK_DURATION_MS, 1);
        // Ease-out cubic for smooth deceleration
        const ease = 1 - (1 - t) * (1 - t) * (1 - t);

        camera.radius = startRadius + (endRadius - startRadius) * ease;

        if (startColor && endColor) {
            const r = startColor.r + (endColor.r - startColor.r) * ease;
            const g = startColor.g + (endColor.g - startColor.g) * ease;
            const b = startColor.b + (endColor.b - startColor.b) * ease;
            scene.clearColor = new Color4(r, g, b, 1);
        }

        if (t >= 1) {
            scene.onBeforeRenderObservable.remove(observer);
        }
    });
}
